import React, { Fragment } from 'react';
import { InputFileSupport } from '@/features/support/components/InputFileSupport';
import { useHistory } from 'react-router-dom';
import { FormData, FormErrors, OnChangeFormInput } from '@/hooks';
import {
  FaPaperPlane,
  FaRegUser,
  FaSyncAlt,
  FaCopy,
  FaShareAlt,
  FaBolt,
  FaReceipt,
} from 'react-icons/fa';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { toast } from 'react-toastify';
import {
  FormInputMessagem,
  FormUpdateSupportStatus,
  ShouldShowModalReverse,
  States,
} from '@/features/support/types';
import Support from '@/model/Support';
import MessageType from '@/model/MessageType';
import SenderType from '@/model/SenderType';
import { Loading, SelectCustom, TextArea, Dialog } from '@/components';
import { updateMask } from '@/helpers/masks/cpf';
import SupportType from '@/model/SupportType';
import { ReverseDetailContent } from '@/features/reverse/components/ReverseDetailContent';
import OrderSite from '@/model/OrderSite';
import IconCloseCircle from '@/assets/images/svg/close';
import dayjs from 'dayjs';
import { QuickReply } from '@/features/support/components/QuickRepliesPanel/QUICK_REPLIES';
import { QuickRepliesPanel } from '@/features/support/components/QuickRepliesPanel';
import { ClientInfo } from '@/features/support/components/ClientInfo';
import './style.css';

const customRenderer = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  link(this: any, token: any) {
    const text = this.parser.parseInline(token.tokens);
    return `<a href="${token.href}" ${
      token.title ? `title="${token.title}"` : ''
    } target="_blank" rel="noopener noreferrer">${text}</a>`;
  },
};

marked.use({ renderer: customRenderer, breaks: true, gfm: true });

const renderMarkdown = (text: string): string => {
  if (!text) return '';
  return DOMPurify.sanitize(marked.parse(text) as string);
};

interface StateProps {
  state: States;
  support?: Support;
  formData: FormData;
  formErrors: FormErrors;
  formDataStatus: FormData;
  formErrorsStatus: FormErrors;
  currentPage: OrderSite[];
  order?: OrderSite;
  visible: boolean;
  modal: ShouldShowModalReverse;
  title: string | React.ReactNode;
  suggestedReplies: QuickReply[];
}

interface OptionProps {
  value: string;
  label: string;
}

interface DispatchProps {
  onChangeInput: OnChangeFormInput;
  onChangeFileInput: (e: EventTarget & HTMLInputElement) => void;
  onClearImage: () => void;
  onSendMessage: () => void;
  onUpdateStatusConfirm: () => void;
  onChangeFormInput: OnChangeFormInput;
  onShouldShowModal: (
    value: ShouldShowModalReverse,
    newTitleModal: string | React.ReactNode,
    orderSelected?: OrderSite,
  ) => void;
  onShowReverseOrder: (orderSelected: OrderSite) => void;
  onShowCancelOrder: (orderSelected: OrderSite) => void;
  onToggle: () => void;
  onUseQuickReply: (message: string) => void;
}

type Props = StateProps & DispatchProps;

export const ChatUI: React.FC<Props> = (props): JSX.Element => {
  const {
    state,
    support,
    formData,
    formErrors,
    formDataStatus,
    formErrorsStatus,
    suggestedReplies,
    onChangeInput,
    onChangeFileInput,
    onClearImage,
    onSendMessage,
    onChangeFormInput,
    onUpdateStatusConfirm,
    onUseQuickReply,
    currentPage,
    order,
    visible,
    modal,
    title,
    onShouldShowModal,
    onShowReverseOrder,
    onShowCancelOrder,
    onToggle,
  } = props;
  const history = useHistory();
  const imageBase64Ref = React.useRef<HTMLInputElement>(null);
  const [showClientDetails, setShowClientDetails] = React.useState(true);
  const [showQuickReplies, setShowQuickReplies] = React.useState(false);
  const [showOrders, setShowOrders] = React.useState(false);
  const disableInput = (): boolean => {
    if (formData.file && String(formData.file).trim().length > 0) {
      return true;
    }
    return false;
  };

  const isPDF = (content: string): boolean => {
    const split = content.split('.');
    const extension = split[split.length - 1];
    if (extension.toUpperCase() === 'PDF') {
      return true;
    }
    return false;
  };

  const getType = (supportType: SupportType): string => {
    let value = '';
    if (supportType === SupportType.CANCELLATION) {
      value = 'Cancelamento';
    } else if (supportType === SupportType.PAYMENTS) {
      value = 'Pagamento';
    } else if (supportType === SupportType.TICKET) {
      value = 'Ingresso';
    } else if (supportType === SupportType.ACCOUNT) {
      value = 'Conta';
    } else if (supportType === SupportType.OTHERS) {
      value = 'Outros';
    }
    return value;
  };

  const copyToClipboard = (text: string, label: string): void => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado com sucesso!`);
  };

  const shareAllClientInfo = (): void => {
    if (!support) return;
    const cpfFormatted = support.cpf ? updateMask(support.cpf) : '';
    const category = getType(support.supportType);
    const text =
      `*Informações do Cliente - Protocolo #${support.protocol}*\n` +
      `• *Evento:* ${support.event?.name || 'Não informado'}\n` +
      `• *Categoria:* ${category || 'Não informada'}\n` +
      `• *Nome:* ${support.name || 'Não informado'}\n` +
      `• *CPF:* ${cpfFormatted || 'Não informado'}\n` +
      `• *E-mail:* ${support.email || 'Não informado'}`;
    navigator.clipboard.writeText(text);
    toast.success('Ficha completa copiada!');
  };

  const optionsSelect: OptionProps[] = [
    { value: 'Reaberto', label: 'Abrir Novamente' },
    { value: 'Aguardando Cliente', label: 'Aguardando Cliente' },
    { value: 'Cancelado', label: 'Cancelar' },
    { value: 'Fechado', label: 'Fechar' },
    { value: 'Resolvido', label: 'Foi Resolvido' },
  ];

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSendMessage();
    }
  };

  return (
    <>
      <Loading isVisible={state === States.loading} />
      <div className="main-chat-layout">
        <div className="container-chat-full">
          <div className="chat-main-column">
            <div className="chat-header-top">
              <button
                type="button"
                className="btn-back-to-list"
                onClick={() => history.push('/atendimento-suporte')}
                title="Voltar para a lista de atendimentos"
              >
                ← Voltar para Atendimentos
              </button>
            </div>
            <div className="messages-container">
              {support &&
                support.messages &&
                support.messages.length > 0 &&
                [...support.messages]
                  .sort(
                    (a, b) => new Date(a.messageDate).getTime() - new Date(b.messageDate).getTime(),
                  )
                  .map((message, index) => (
                    <Fragment key={`${index}-message-${message.id}`}>
                      {message.messageType === MessageType.TEXT ? (
                        <div
                          key={index}
                          className={`message ${
                            message.senderType !== SenderType.CLIENT
                              ? 'sender-user'
                              : 'sender-other'
                          }`}
                        >
                          <span
                            className="message-markdown"
                            dangerouslySetInnerHTML={{ __html: renderMarkdown(message.comment) }}
                          />
                          <div
                            className={`hour-message text-right ${
                              message.senderType === SenderType.CLIENT ? '' : 'text-white-500'
                            } `}
                          >
                            {dayjs(message.messageDate)
                              .add(3, 'hour')
                              .locale('pt-br')
                              .format('DD/MM/YYYY - HH:mm')}
                          </div>
                        </div>
                      ) : (
                        <Fragment>
                          {isPDF(message.comment) ? (
                            <div
                              key={index}
                              className={`message ${
                                message.senderType !== SenderType.CLIENT
                                  ? 'sender-user'
                                  : 'sender-other'
                              }`}
                            >
                              <pre>
                                <iframe
                                  src={`https://drive.google.com/viewerng/viewer?embedded=true&url=${message.comment}#toolbar=0&scrollbar=0`}
                                  style={{ width: '100%', height: '100%' }}
                                  frameBorder="0"
                                ></iframe>
                              </pre>
                              <div
                                className={`hour-message text-right ${
                                  message.senderType === SenderType.CLIENT ? '' : 'text-white-500'
                                } `}
                              >
                                {dayjs(message.messageDate)
                                  .add(3, 'hour')
                                  .locale('pt-br')
                                  .format('DD/MM/YYYY - HH:mm')}
                              </div>
                            </div>
                          ) : (
                            <div
                              key={index}
                              className={`message ${
                                message.senderType !== SenderType.CLIENT
                                  ? 'sender-user'
                                  : 'sender-other'
                              }`}
                            >
                              <img src={message.comment} alt="Imagem selecionada" />
                              <div
                                className={`hour-message ${
                                  message.senderType !== SenderType.CLIENT ? '' : 'text-white-500'
                                } text-right`}
                              >
                                {dayjs(message.messageDate)
                                  .add(3, 'hour')
                                  .locale('pt-br')
                                  .format('DD/MM/YYYY - HH:mm')}
                              </div>
                            </div>
                          )}
                        </Fragment>
                      )}
                    </Fragment>
                  ))}
            </div>
            <div className="chat-composer">
              <div className="chat-action-tray">
                <button
                  type="button"
                  className={`btn-composer-action ${showClientDetails ? 'active' : ''}`}
                  onClick={() => setShowClientDetails(!showClientDetails)}
                  title={
                    showClientDetails ? 'Ocultar Dados do Cliente' : 'Mostrar Dados do Cliente'
                  }
                >
                  <FaRegUser size={16} />
                </button>
                <button
                  type="button"
                  className={`btn-composer-action ${showQuickReplies ? 'active' : ''}`}
                  onClick={() => {
                    const nextVal = !showQuickReplies;
                    setShowQuickReplies(nextVal);
                    if (nextVal) {
                      setShowOrders(false);
                    }
                  }}
                  title={
                    showQuickReplies ? 'Ocultar Respostas Rápidas' : 'Mostrar Respostas Rápidas'
                  }
                >
                  <FaBolt size={16} />
                </button>
                <button
                  type="button"
                  className={`btn-composer-action ${showOrders ? 'active' : ''}`}
                  onClick={() => {
                    const nextVal = !showOrders;
                    setShowOrders(nextVal);
                    if (nextVal) {
                      setShowQuickReplies(false);
                    }
                  }}
                  title={showOrders ? 'Ocultar Pedidos' : 'Mostrar Pedidos'}
                >
                  <FaReceipt size={16} />
                </button>
              </div>
              <div className="chat-input-area">
                {formData.file && (
                  <button type="button" onClick={onClearImage} className="clearImage">
                    <IconCloseCircle />
                  </button>
                )}

                <TextArea
                  name="sendmessage"
                  value={String(formData[FormInputMessagem.message] || '')}
                  onChange={e => onChangeInput(FormInputMessagem.message)(e.target.value)}
                  onKeyDown={onKeyDown}
                  error={formErrors.message && formErrors.message[0]}
                  wrapperClass="chat-textarea-wrapper"
                  className="chat-textarea"
                  placeholder="Digite sua mensagem"
                  rows={2}
                  disabled={disableInput()}
                />

                <div className="chat-attach-button">
                  <InputFileSupport
                    refInput={imageBase64Ref}
                    name="file"
                    onChange={e => onChangeFileInput(e.target)}
                    error={formErrors.file && formErrors.file[0]}
                  />
                </div>
              </div>

              <button type="button" className="chat-send-button" onClick={onSendMessage}>
                <FaPaperPlane />
              </button>
            </div>
            {showQuickReplies && (
              <div className="quick-replies-wrapper">
                <QuickRepliesPanel onSelect={onUseQuickReply} replies={suggestedReplies} />
              </div>
            )}
            {showOrders && (
              <div className="orders-panel-wrapper-inline">
                <ClientInfo
                  state={state}
                  currentPage={currentPage}
                  onShouldShowModal={onShouldShowModal}
                  title={title}
                  visible={visible}
                  onToggle={() => setShowOrders(false)}
                  modal={modal}
                  order={order}
                  onShowReverseOrder={onShowReverseOrder}
                  onShowCancelOrder={onShowCancelOrder}
                />
              </div>
            )}
          </div>
          {showClientDetails && (
            <div className="client-details-drawer">
              <div className="client-info-header">
                <span className="client-info-title">
                  <FaRegUser /> Dados do cliente
                </span>
                <button
                  type="button"
                  className="btn-share-all"
                  onClick={shareAllClientInfo}
                  title="Copiar todas as informações formatadas"
                >
                  <FaShareAlt /> Compartilhar
                </button>
              </div>
              <div className="client-info-list">
                <div className="client-info-card">
                  <span className="client-info-label">Evento</span>
                  <div className="client-info-value-container">
                    <span className="client-info-value">
                      {support?.event?.name || 'Não informado'}
                    </span>
                    <button
                      type="button"
                      className="btn-copy-info"
                      onClick={() => copyToClipboard(support?.event?.name || '', 'Nome do Evento')}
                      title="Copiar Evento"
                    >
                      <FaCopy size={12} />
                    </button>
                  </div>
                </div>

                <div className="client-info-card">
                  <span className="client-info-label">Protocolo</span>
                  <div className="client-info-value-container">
                    <span className="client-info-value">#{support?.protocol}</span>
                    <button
                      type="button"
                      className="btn-copy-info"
                      onClick={() => copyToClipboard(support?.protocol || '', 'Protocolo')}
                      title="Copiar Protocolo"
                    >
                      <FaCopy size={12} />
                    </button>
                  </div>
                </div>

                <div className="client-info-card">
                  <span className="client-info-label">Categoria</span>
                  <div className="client-info-value-container">
                    <span className="client-info-value">
                      {support ? getType(support.supportType) : 'Não informada'}
                    </span>
                    <button
                      type="button"
                      className="btn-copy-info"
                      onClick={() =>
                        copyToClipboard(
                          support ? getType(support.supportType) : '',
                          'Categoria do Suporte',
                        )
                      }
                      title="Copiar Categoria"
                    >
                      <FaCopy size={12} />
                    </button>
                  </div>
                </div>

                <div className="client-info-card">
                  <span className="client-info-label">Nome do Cliente</span>
                  <div className="client-info-value-container">
                    <span className="client-info-value">{support?.name || 'Não informado'}</span>
                    <button
                      type="button"
                      className="btn-copy-info"
                      onClick={() => copyToClipboard(support?.name || '', 'Nome do Cliente')}
                      title="Copiar Nome"
                    >
                      <FaCopy size={12} />
                    </button>
                  </div>
                </div>

                <div className="client-info-card">
                  <span className="client-info-label">CPF</span>
                  <div className="client-info-value-container">
                    <span className="client-info-value">
                      {support && support.cpf ? updateMask(support.cpf) : 'Não informado'}
                    </span>
                    <button
                      type="button"
                      className="btn-copy-info"
                      onClick={() =>
                        copyToClipboard(
                          support && support.cpf ? updateMask(support.cpf) : '',
                          'CPF',
                        )
                      }
                      title="Copiar CPF"
                    >
                      <FaCopy size={12} />
                    </button>
                  </div>
                </div>

                <div className="client-info-card">
                  <span className="client-info-label">E-mail</span>
                  <div className="client-info-value-container">
                    <span className="client-info-value">{support?.email || 'Não informado'}</span>
                    <button
                      type="button"
                      className="btn-copy-info"
                      onClick={() => copyToClipboard(support?.email || '', 'E-mail')}
                      title="Copiar E-mail"
                    >
                      <FaCopy size={12} />
                    </button>
                  </div>
                </div>

                <div className="client-info-card">
                  <span className="client-info-label">Alerta</span>
                  <div className="client-info-value-container">
                    <span
                      className="client-info-value"
                      style={{
                        color:
                          support && support.client && support.client.fraudAlert
                            ? '#ef4444'
                            : '#10b981',
                        fontWeight: 600,
                      }}
                    >
                      {support && support.client && support.client.fraudAlert
                        ? 'Com Alerta'
                        : 'Sem Alerta'}
                    </span>
                  </div>
                </div>

                <div className="status-container-horizontal">
                  <span className="status-warning-label">MUDAR STATUS:</span>
                  <SelectCustom
                    name="supportStatus"
                    placeholder={formDataStatus[FormUpdateSupportStatus.status]}
                    value={formDataStatus[FormUpdateSupportStatus.status]}
                    options={optionsSelect}
                    onChange={e => {
                      onChangeFormInput(FormUpdateSupportStatus.status)(e?.value as string);
                    }}
                    error={formErrorsStatus.type && formErrorsStatus.type[0]}
                  />
                  <button
                    onClick={() => onUpdateStatusConfirm()}
                    className="update-button-horizontal"
                    title="Salvar Status"
                  >
                    <FaSyncAlt size={12} /> Salvar Status
                  </button>
                </div>
              </div>
            </div>
          )}{' '}
        </div>
      </div>
      <Dialog
        title={title}
        visible={visible && modal === ShouldShowModalReverse.detail}
        onClose={onToggle}
        position={'center'}
        isContentWithCard={true}
        actions={[
          {
            title: 'Fechar',
            onClick: onToggle,
            theme: 'noneBorder',
          },
          {
            title: 'OK',
            onClick: onToggle,
          },
        ]}
      >
        <ReverseDetailContent order={order} />
      </Dialog>
    </>
  );
};
